import { useState } from 'react';
import { Link } from 'react-router';
import type { NewsPost } from '@gl/shared';

import { useDeleteNewsPost, useNews } from '../api/hooks.js';
import { useCan } from '../auth.js';
import { EmptyState, ErrorState, Loading } from '../components/states.js';
import { Markdown } from '../components/Markdown.js';
import { PostEditor } from '../components/PostEditor.js';
import { formatDate } from '../util/format.js';

export function NewsPage() {
  const { data, isPending, error } = useNews();
  const canEdit = useCan('edit_posts');
  const remove = useDeleteNewsPost();
  const [editing, setEditing] = useState<NewsPost | 'new' | null>(null);

  return (
    <div>
      <div className="page-head flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow mb-1">The league</p>
          <h1 className="page-title">News</h1>
        </div>
        {canEdit ? (
          <button type="button" className="btn-primary" onClick={() => setEditing('new')}>
            New post
          </button>
        ) : null}
      </div>

      {isPending ? <Loading /> : null}
      {error ? <ErrorState error={error} /> : null}

      {data?.length === 0 ? <EmptyState>No posts have been written yet.</EmptyState> : null}

      {/* Every post in full. The page is the news, not an index of it. */}
      <div className="space-y-5">
        {data?.map((post) => (
          <article key={post.id} className="card border-l-2 border-l-brass-400">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <Link
                  to={`/news/${post.slug}`}
                  className="font-display text-3xl font-bold uppercase leading-none tracking-tight
                      text-field-50 hover:text-brass-300"
                >
                  {post.title}
                </Link>
                <p className="mt-1 text-xs text-field-500">
                  {post.authorName}
                  {post.publishedAt ? ` · ${formatDate(post.publishedAt)}` : ''}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {post.audience === 'public' ? (
                  <span className="badge border border-field-700 text-field-300">Public</span>
                ) : (
                  <span className="badge border border-field-700 text-field-500">Members</span>
                )}
                {!post.publishedAt ? (
                  <span className="badge bg-brass-700/30 text-brass-200">Draft</span>
                ) : null}
              </div>
            </div>

            <div className="mt-3">
              <Markdown>{post.body}</Markdown>
            </div>

            {canEdit ? (
              <div className="mt-4 flex gap-2">
                <button type="button" className="btn-secondary" onClick={() => setEditing(post)}>
                  Edit
                </button>
                <button
                  type="button"
                  className="btn-danger"
                  onClick={() => {
                    if (confirm(`Delete "${post.title}"?`)) remove.mutate(post.id);
                  }}
                >
                  Delete
                </button>
              </div>
            ) : null}
          </article>
        ))}
      </div>

      {editing ? (
        <PostEditor post={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />
      ) : null}
    </div>
  );
}
